import { createContext, useCallback, useContext, useReducer, ReactNode, Reducer } from 'react'
import { FeedbackReponseType, JSONDocNode } from '../types/support'
import { defaultDocNode, defaultFeatureSuggestionState } from '../utils/support'

export const defaultAdfValue: JSONDocNode = {
  version: 1,
  type: 'doc',
  content: []
}

enum SupportPageActionType {
  SET_EXISTING_FEATURES = 'SET_EXISTING_FEATURES',
  SET_FEATURE_DIALOG_OPEN = 'SET_FEATURE_DIALOG_OPEN',
  SET_FEATURE_SUGGESTION_FORM_STATE = 'SET_FEATURE_SUGGESTION_FORM_STATE',
  SET_VIEW_FEATURE_DIALOG_OPEN = 'SET_VIEW_FEATURE_DIALOG_OPEN',
  SET_DELETE_FORM_DIALOG_OPEN = 'SET_DELETE_FORM_DIALOG_OPEN'
}

type SupportPageAction =
  | { type: SupportPageActionType.SET_EXISTING_FEATURES; payload: { data: any } }
  | { type: SupportPageActionType.SET_FEATURE_DIALOG_OPEN; payload: { open: boolean } }
  | { type: SupportPageActionType.SET_FEATURE_SUGGESTION_FORM_STATE; payload: FeedbackReponseType }
  | { type: SupportPageActionType.SET_VIEW_FEATURE_DIALOG_OPEN; payload: boolean }
  | { type: SupportPageActionType.SET_DELETE_FORM_DIALOG_OPEN; payload: boolean }

interface SupportPageContextType {
  state: SupportPageType
  actions: {
    handleFeatureSuggestionDialog: (isOpen: boolean) => void
    handleFeatureSuggestion: (data: FeedbackReponseType) => void
    handleSetViewFeatureDialogOpen: (data: boolean) => void
    handleSetDeleteTicketDialogOpen: (data: boolean) => void
  }
  getters: {}
}

type SupportPageType = {
  isFeatureDialogOpen: boolean
  featureSuggestionForm: FeedbackReponseType
  isViewFeatureDialogOpen: boolean
  isDeleteTicketDialogOpen: boolean
}

const initialState: SupportPageType = {
  isFeatureDialogOpen: false,
  featureSuggestionForm: defaultFeatureSuggestionState,
  isViewFeatureDialogOpen: false,
  isDeleteTicketDialogOpen: false
}

function supportPageReducer(state: SupportPageType, action: SupportPageAction): SupportPageType {
  switch (action.type) {
    case SupportPageActionType.SET_FEATURE_DIALOG_OPEN:
      return {
        ...state,
        isFeatureDialogOpen: action.payload.open
      }
    case SupportPageActionType.SET_FEATURE_SUGGESTION_FORM_STATE:
      return {
        ...state,
        featureSuggestionForm: action.payload
      }
    case SupportPageActionType.SET_VIEW_FEATURE_DIALOG_OPEN:
      return {
        ...state,
        isViewFeatureDialogOpen: action.payload
      }
    case SupportPageActionType.SET_DELETE_FORM_DIALOG_OPEN:
      return {
        ...state,
        isDeleteTicketDialogOpen: action.payload
      }
    default:
      return state
  }
}

const SupportPageContext = createContext<SupportPageContextType | undefined>(undefined)

const SupportPageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer<Reducer<SupportPageType, SupportPageAction>>(supportPageReducer, initialState)

  const handleFeatureSuggestionDialog = useCallback((isOpen: boolean) => {
    dispatch({
      type: SupportPageActionType.SET_FEATURE_DIALOG_OPEN,
      payload: { open: isOpen }
    })
  }, [])

  const handleFeatureSuggestion = useCallback((data: FeedbackReponseType) => {
    dispatch({
      type: SupportPageActionType.SET_FEATURE_SUGGESTION_FORM_STATE,
      payload: data
    })
  }, [])

  const handleSetViewFeatureDialogOpen = useCallback((data: boolean) => {
    dispatch({
      type: SupportPageActionType.SET_VIEW_FEATURE_DIALOG_OPEN,
      payload: data
    })
  }, [])

  const handleSetDeleteTicketDialogOpen = useCallback((data: boolean) => {
    dispatch({
      type: SupportPageActionType.SET_DELETE_FORM_DIALOG_OPEN,
      payload: data
    })
  }, [])

  const actions = {
    handleFeatureSuggestionDialog,
    handleFeatureSuggestion,
    handleSetViewFeatureDialogOpen,
    handleSetDeleteTicketDialogOpen
  }

  const getters = {}

  return <SupportPageContext.Provider value={{ state, actions, getters }}>{children}</SupportPageContext.Provider>
}

const useSupportPageContext = (): SupportPageContextType => {
  const context = useContext(SupportPageContext)
  if (!context) {
    throw new Error('useSupportPageContext must be used within a SupportPageProvider')
  }
  return context
}

export { SupportPageProvider, useSupportPageContext }
